import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import arrowLeft from "../assets/images/arrow-left.png";
import progressImage from "../assets/images/progressreport.webp";
import { Link, useParams } from 'react-router-dom';
import { getPrograms, getSubPrograms } from '../services/ProgramApi';
import { getChildFeedback } from '../services/TeacherApi';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';
import StarRating from './StartRating';

const ProgressReport = () => {
  const { id: childId } = useParams();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [programs, setPrograms] = useState([]);
  const [subPrograms, setSubPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedSubProgram, setSelectedSubProgram] = useState("");
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const years = Array.from({ length: 30 }, (_, i) => currentYear + i);

  useEffect(() => {
    fetchPrograms();
    fetchSubPrograms();
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear && selectedProgram) {
      fetchChildFeedback();
    }
  }, [selectedMonth, selectedYear, selectedProgram, selectedSubProgram]);

  const fetchPrograms = async () => {
    try {
      const response = await getPrograms();
      if (response.data.data && Array.isArray(response.data.data)) {
        setPrograms(response.data.data);
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch programs");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubPrograms = async () => {
    try {
      const response = await getSubPrograms();
      if (response.data.data && Array.isArray(response.data.data)) {
        const subProgramOptions = response.data.data.flatMap((subProgram) =>
          subProgram.images.map((image) => ({
            id: image.id,
            title: image.title,
            program_id: subProgram.program_id,
          }))
        );
        setSubPrograms(subProgramOptions);
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch subprograms");
    } finally {
      setLoading(false);
    }
  };

  const fetchChildFeedback = async () => {
    try {
      setLoading(true);
      const response = await getChildFeedback({
        child_id: childId,
        month: selectedMonth,
        year: selectedYear,
        program_id: selectedProgram,
        focus_id: selectedSubProgram || undefined,
      });

      if (response.data.status === 200) {
        setFeedbackData(response.data.data[0]);
      } else {
        setFeedbackData(null);
        toast.error(response.data.message || "Failed to fetch feedback");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch feedback");
    } finally {
      setLoading(false);
    }
  };

  const filteredSubPrograms = subPrograms.filter(
    (subProgram) => subProgram.program_id === parseInt(selectedProgram)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='app-container mobile-bottom'>
      <Container className='mb-4'>
        <div className='breadcrum bg_light_yellow'>
          <h2 className='d-flex align-items-center mb-0 fs-20'>
            <Link to='/dashboard'><img src={arrowLeft} width={18} className='me-2' alt="link" /></Link>
            Progress Report</h2>
        </div>
        <div className='my-4 position-relative'>
          <img src={progressImage} className='img-fluid' alt="link" />
          <div className='banneCaption'>
            <h6 className='mb-0 fw-7 text-danger'>Great Job!</h6>
            <p className='fs-10'>Check out the month’s performance and feedback</p>
          </div>
        </div>

        <small className='fs-12 text-danger'>Select month, year, and course to view past progress reports</small>
        <Row className='my-2'>
          <Col xs='3'>
            <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} aria-label="month" className='bg-danger px-2 text-white mb-3 fs-12'>
              <option value="">Month</option>
              {months.map((month, index) => (
                <option value={index + 1} key={index}>{month}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs='3'>
            <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} aria-label="year" className='bg-danger px-2 text-white mb-3 fs-12'>
              <option value="">Year</option>
              {years.map((year, index) => (
                <option value={year} key={index}>{year}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs='3'>
            <Form.Select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} aria-label="program" className='bg-danger px-2 text-white mb-3 fs-12'>
              <option value="">Select Program</option>
              {programs.map((program, index) => (
                <option key={index} value={program.id}>{program.program_name}</option>
              ))}
            </Form.Select>
          </Col>
          {filteredSubPrograms.length > 0 && (
            <Col xs='3'>
              <Form.Select value={selectedSubProgram} onChange={(e) => setSelectedSubProgram(e.target.value)} aria-label="subprogram" className='bg-danger px-2 text-white mb-3 fs-12'>
                <option value="">Select Sub Program</option>
                {filteredSubPrograms.map((subProgram) => (
                  <option key={subProgram.id} value={subProgram.id}>{subProgram.title}</option>
                ))}
              </Form.Select>
            </Col>
          )}
        </Row>

        {feedbackData ? (
          <>
            <p className='fw-5 mb-1 fs-10'>Report for: {selectedMonth ? selectedMonth : "Month"}, {selectedYear ? selectedYear : "Year"}</p>
            <h5 className='text-danger fw-6 fs-14'>Teachers Comments</h5>
            <p className='border rounded-3 border-dark p-3 fs-12 fw-5 text-center'>{feedbackData?.comments}</p>
            <div className='border-2 my-4 border-bottom'></div>

            <Row className='my-2 justify-content-center'>
              <Col xs='6'>
                <div className='ratingSection mb-3'>
                  <h6 className='text-danger fs-14 fw-6'>Attendance</h6>
                  <div className='startSection rounded-1 bg_dark_green p-2 d-flex justify-content-between'>
                    <StarRating rating={feedbackData?.attendance_rating || 0} />
                  </div>
                  <small className='fs-9 fw-5'>Great Attendance, but missed a couple of classes.</small>
                </div>
              </Col>
              <Col xs='6'>
                <div className='ratingSection mb-3'>
                  <h6 className='text-danger fs-14 fw-6'>Participation</h6>
                  <div className='startSection rounded-1 bg_dark_purple p-2 d-flex justify-content-between'>
                    <StarRating rating={feedbackData?.participation_rating || 0} />
                  </div>
                  <small className='fs-9 fw-5'>Moderate participation, can contribute more.</small>
                </div>
              </Col>
              <Col xs='6'>
                <div className='ratingSection mb-3'>
                  <h6 className='text-danger fs-14 fw-6'>Skill Improvement</h6>
                  <div className='startSection rounded-1 blue3 p-2 d-flex justify-content-between'>
                    <StarRating rating={feedbackData?.skill_improvement_rating || 0} />
                  </div>
                  <small className='fs-9 fw-5'>Excellent progress skills are improving rapidly.</small>
                </div>
              </Col>
              <Col xs='6'>
                <div className='ratingSection mb-3'>
                  <h6 className='text-danger fs-14 fw-6'>Focus & Discipline</h6>
                  <div className='startSection rounded-1 bg_dark_blue p-2 d-flex justify-content-between'>
                    <StarRating rating={feedbackData?.focus_discipline_rating || 0} />
                  </div>
                  <small className='fs-9 fw-5'>Good focus overall, but occasional distraction.</small>
                </div>
              </Col>
              <Col xs='6'>
                <div className='ratingSection'>
                  <h6 className='text-danger fs-14 fw-6'>Creativity</h6>
                  <div className='startSection rounded-1 bg_dark_blue p-2 d-flex justify-content-between'>
                    <StarRating rating={feedbackData?.creativity_rating || 0} />
                  </div>
                  <small className='fs-9 fw-5'>Highly Creative and expressive, stands out in class.</small>
                </div>
              </Col>
            </Row>
            <div className='border-2 my-4 border-bottom'></div>
            <Row className=''>
              <Col xs='6'>
                <h6 className='text-danger fs-14 fw-6'>This Month's Milestones</h6>
                <ul className='progressList border rounded-3 border-dark p-3 fw-5 fs-12'>
                  <li>{feedbackData?.milestone}</li>
                </ul>
              </Col>
              <Col xs='6'>
                <h6 className='text-danger fs-14 fw-6'>Goals for the Next Month</h6>
                <ul className='progressList border rounded-3 border-dark p-3 fs-12 fw-5'>
                  <li>{feedbackData?.next_month_goals}</li>
                </ul>
              </Col>
            </Row>
          </>
        ) : (
          <div className='text-center my-4'>
            <p className='text-muted fs-12'>No feedback data available for this selection.</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ProgressReport;